import { ProCard } from '@components'
import proCardStyles from '@components/DataDisplay/ProCard/ProCard.module.scss'
import DonutChart from '@dashboard/components/DonutChart'
import type { DonutSegment } from '@dashboard/types/analytics.types'
import cn from '@integrations/classnames'
import { createContext, type ReactNode, useContext } from '@integrations/react'
import skeletonStyles from '../DashboardSkeleton/DashboardSkeleton.module.scss'

interface DonutCardContextValue {
  isLoading: boolean
}

const DonutCardContext = createContext<DonutCardContextValue>({ isLoading: false })

interface DonutCardProps {
  isLoading: boolean
  className?: string
  children: ReactNode
}

function DonutCard({ isLoading, className, children }: DonutCardProps) {
  return (
    <DonutCardContext.Provider value={{ isLoading }}>
      <ProCard className={cn('donut-card', className)}>{children}</ProCard>
    </DonutCardContext.Provider>
  )
}

interface HeaderProps {
  title: string
  subtitle?: string
}

function Header({ title, subtitle }: HeaderProps) {
  return (
    <div className={cn(proCardStyles['pro-card__title-row'], 'pro-card__title-row')}>
      <h3 className={cn(proCardStyles['pro-card__title'], 'pro-card__title')}>{title}</h3>
      {subtitle && <p className={cn(proCardStyles['pro-card__subtitle'], 'pro-card__subtitle')}>{subtitle}</p>}
    </div>
  )
}

interface BodyProps {
  segments: DonutSegment[]
  emptyMessage?: string
  innerLabel?: string
  innerCaption?: string
  skeletonLines?: number
  children?: ReactNode
}

function Body({ segments, emptyMessage, innerLabel, innerCaption, skeletonLines = 5, children }: BodyProps) {
  const { isLoading } = useContext(DonutCardContext)

  if (isLoading) {
    return (
      <div className={cn(proCardStyles['pro-card__skeleton-row'], 'pro-card__skeleton-row')}>
        <div className={cn(proCardStyles['pro-card__skeleton-circle'], 'pro-card__skeleton-circle')} />
        <div className={cn(proCardStyles['pro-card__skeleton-lines'], 'pro-card__skeleton-lines')}>
          {Array.from({ length: skeletonLines }, (_, i) => i).map((i) => (
            <div
              key={`sk-${i}`}
              className={cn(
                proCardStyles['pro-card__skeleton-line'],
                skeletonStyles['dashboard-skeleton'],
                skeletonStyles['dashboard-skeleton-animated'],
                'pro-card__skeleton-line'
              )}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={cn(proCardStyles['pro-card__body-row'], 'pro-card__body-row')}>
      <div className={cn(proCardStyles['pro-card__donut'], 'pro-card__donut')}>
        <DonutChart segments={segments} innerLabel={innerLabel} innerCaption={innerCaption} />
      </div>
      <div className={cn(proCardStyles['pro-card__legend'], 'pro-card__legend')}>
        {children ??
          (segments.length === 0
            ? // No data still shows the chart: the bare track ring, with the message in the legend slot.
              emptyMessage && (
                <div className={cn(proCardStyles['pro-card__legend-item'], 'pro-card__legend-item')}>
                  {emptyMessage}
                </div>
              )
            : segments.map((seg) => (
                <div key={seg.label} className={cn(proCardStyles['pro-card__legend-item'], 'pro-card__legend-item')}>
                  <div className={cn(proCardStyles['pro-card__legend-label'], 'pro-card__legend-label')}>
                    <span
                      className={cn(proCardStyles['pro-card__legend-dot'], 'pro-card__legend-dot')}
                      style={{ background: seg.color }}
                    />
                    {seg.label}
                  </div>
                  <span className={cn(proCardStyles['pro-card__legend-pct'], 'pro-card__legend-pct')}>
                    {Math.round(seg.percentage)}%
                  </span>
                </div>
              )))}
      </div>
    </div>
  )
}

DonutCard.Header = Header
DonutCard.Body = Body

export default DonutCard
